import React, { createContext, useMemo, useCallback } from 'react'
import { executeWithRetry } from '../../Utils/Networking/networkHelpers'
import { logEvent } from '../../Utils/analytics/firAnalytics'
import { normalizeError } from '../../Utils/errors/errorHelpers'
import { ANALYTICS_TAGS } from '../../Constants/analytics'
import { useCacheHelpers } from '../../Hooks'
import { useStorageReady } from '../../Hooks/storage/useStorageReady'
import { indexedDBs } from '../../Config/Storage/storage.config'
import type { StorageContextType } from './types'

export const StorageContext = createContext<StorageContextType | null>(null)

export const StorageProvider: React.FC<React.PropsWithChildren<unknown>> = ({
  children,
}) => {
  const {
    isReady: isStorageReady,
    storageStatuses,
    error: storageError,
    isChecking,
  } = useStorageReady()
  const { storeInCache, retrieveFromCache } = useCacheHelpers({
    dbName: indexedDBs.main.dbName,
    storeName: indexedDBs.main.storeName,
    dbVersion: indexedDBs.main.dbVersion,
    ttlMs: indexedDBs.main.ttlMs,
  })

  const store = useCallback(
    async (key: string, item: unknown, ttlMs?: number): Promise<void> => {
      if (!isStorageReady) {
        console.warn(`Storage not ready, skipping store operation for key: ${key}`)
        return
      }

      const result = await executeWithRetry(async () => {
        await storeInCache(key, item, storageStatuses, ttlMs ?? indexedDBs.main.ttlMs)
        return true
      }, `store data with key: ${key}`)

      if (result === null) {
        logEvent(ANALYTICS_TAGS.storage_failure, {
          context: 'storage_retry_max_attempts_exceeded_store',
          key,
        })
      }
    },
    [storageStatuses, storeInCache, isStorageReady]
  )

  const fetchAndCache = useCallback(
    async <T,>(
      key: string,
      fetcher: () => Promise<T>,
      onUpdate: (data: T) => void,
      ttlMs: number = indexedDBs.main.ttlMs
    ): Promise<void> => {
      let networkRequestFinished = false

      try {
        const cacheOperation = isStorageReady
          ? retrieveFromCache(key, storageStatuses, ttlMs)
              .then((result) => {
                if (result?.data && !networkRequestFinished) {
                  onUpdate(result.data as T)
                }
              })
              .catch((error) => {
                console.warn('Cache retrieval failed:', error)
              })
          : Promise.resolve()

        const networkOperation = fetcher()
          .then(async (freshData: T) => {
            networkRequestFinished = true
            onUpdate(freshData)
            if (isStorageReady) {
              await store(key, freshData, ttlMs)
            }
            return freshData
          })
          .catch((error) => {
            networkRequestFinished = true
            const normalizedError = normalizeError(error)
            logEvent(ANALYTICS_TAGS.network_request_failed, {
              context: 'network_fetch_failed',
              key,
              error: normalizedError.message,
            })
            return null
          })

        await Promise.all([cacheOperation, networkOperation])
      } catch (outerErr) {
        console.error('fetchAndCache outer error', { key, outerErr })
      }
    },
    [storageStatuses, store, retrieveFromCache, isStorageReady]
  )

  const ctx = useMemo<StorageContextType>(
    () => ({
      storageStatuses,
      isStorageReady,
      isStorageChecking: isChecking,
      storageError,
      fetchAndCache,
    }),
    [storageStatuses, isStorageReady, isChecking, storageError, fetchAndCache]
  )

  return <StorageContext.Provider value={ctx}>{children}</StorageContext.Provider>
}
